import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "./metrics-back-button";
import set from "@rill/set";
import { getChartLabels } from "../../../utils/chart-labels";

import { PERIOD_FORMATS, DATE_FORMATS } from "../../../constants";
import NavigationComponent from "../calendar/components/NavigationComponent";
import SvgIcon from "../../../parts/SvgIcon";
import { AthleteLinks } from "../../../parts/athlete-links";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { useTranslation } from "react-i18next";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_PICKER_FORMAT } = DATE_FORMATS;

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");

  const { exercisesLifts, exercisesVolume, query, search, athlete, pathname } =
    locals;
  const { period, units } = query;
  const organizationCoach = query.organizationCoach || undefined;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesLifts
      : _.pick(exercisesLifts, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = getChartLabels(keys, query, period);
  const liftSeries = _.map(selectedMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });

  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(liftSeries, `data.${i}`))
  );
  const maxVolume = _.max(totalVolumes);

  // duplicate of the lift totals above to get total volume
  // TODO should try to not have to run this twice...maybe?
  const selectedVolumeMetrics =
    query.type === "total"
      ? exercisesVolume
      : _.pick(exercisesVolume, [query.type]);
  const volumeSeries = _.map(selectedVolumeMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });
  const accumulatedVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(volumeSeries, `data.${i}.volume`))
  );

  return (
    <main role="reports-wrapper" className="Main Metrics">
      <NavigationComponent
        query={query}
        pathName={"/athlete-portal/metrics"}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/athlete-portal/metrics/${search}`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/athlete-portal/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/number-of-lifts-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/number-of-lifts-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3 className="lifts-number alt">{t("metrics.numberOfLifts")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("metricsDrawer.kg"), value: "metric" },
                  { text: t("metricsDrawer.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("numberOfLifts.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("numberOfLifts.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("reports.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="end"
                placeholder={t("reports.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("calendar.month"), value: "month" },
                  { text: t("calendar.week"), value: "week" },
                  { text: t("calendar.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("metricsDrawer.kg"), value: "metric" },
                { text: t("metricsDrawer.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("strengthMetricsShared.total"), value: "total" },
                { text: t("strengthMetricsShared.squat"), value: "squat" },
                { text: t("strengthMetricsShared.bench"), value: "bench" },
                { text: t("strengthMetricsShared.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="axis-labels">
                  <div className="left">{t("numberOfLifts.liftsAxisLabel")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: liftSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          referenceValue: 0,
                          high: maxVolume,
                          low: 0,
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: liftSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        chartPadding: { left: 35 },
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          referenceValue: 0,
                          high: maxVolume,
                          low: 0,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              {_.compact(
                                _.map(liftSeries, ({ data, name }) => {
                                  const totalLifts = data[i];
                                  if (!totalLifts) return;
                                  return (
                                    <div key={name}>
                                      {_.capitalize(name)} <b>{totalLifts}</b>
                                    </div>
                                  );
                                })
                              )}
                              <div>
                                {t("numberOfLifts.tooltipTotal")} <b>{totalVolumes[i]}</b>
                              </div>
                              <div>
                                {t("numberOfLifts.tooltipVolume")}{" "}
                                <b>
                                  {locals.withWeight(
                                    locals.fromMetric(
                                      accumulatedVolumes[i],
                                      units
                                    ),
                                    units
                                  )}
                                </b>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

function handleDayChange(date, field) {
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}

const NumberOfLifts = (app) => {
  app.get(
    "/number-of-lifts",
    set("heading", "Number of Lifts"),
    async ({ req, res, load, locals, fail, api }) => {
      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      const { query } = req;

      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesLifts", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
          proxyAthlete: query.athlete,
          reqSource: "metrics",
        }),
        load("exercisesVolume", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
          proxyAthlete: query.athlete,
          reqSource: "metrics",
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "number-of-lifts",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesLifts"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default NumberOfLifts;
